/**
 * Welcome Video Generator Component
 * Generate personalized welcome messages/videos for new admissions
 */
import React, { useState, useEffect } from 'react';
import BulkUploadWelcome from './BulkUploadWelcome';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preview Card Component
const WelcomePreview = ({ data, template, schoolName }) => {
  const generateMessage = () => {
    if (!data.student_name) return '';
    
    const teacherInfo = data.class_teacher ? `${data.student_name} के Class Teacher ${data.class_teacher} होंगे।` : '';
    const firstDayInfo = data.first_day_date ? `पहला दिन ${data.first_day_date} है।` : '';
    
    return `🙏 नमस्ते ${data.parent_name || 'Sir/Ma\'am'} जी!

मैं ${template?.principal_name || 'Principal'}, ${schoolName || 'Our School'} का Principal।

${data.student_name} का ${data.class_name}${data.section ? `-${data.section}` : ''} में admission के लिए बहुत-बहुत बधाई! 🎉

${teacherInfo}
${firstDayInfo}

हम ${data.student_name} के उज्जवल भविष्य के लिए committed हैं।

${schoolName || 'Our School'} परिवार में आपका स्वागत है! 🏫`;
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-6 border-2 border-orange-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white text-xl">
          👨‍💼
        </div>
        <div>
          <h4 className="font-bold">{template?.principal_name || 'Principal'}</h4>
          <p className="text-sm text-gray-500">{schoolName}</p>
        </div>
      </div>
      
      <div className="bg-white rounded-xl p-4 shadow-sm whitespace-pre-line text-gray-700 text-sm leading-relaxed">
        {generateMessage() || 'Student details भरें preview देखने के लिए...'}
      </div>
      
      {data.student_name && (
        <div className="mt-4 p-3 bg-orange-100 rounded-xl">
          <h5 className="font-semibold text-orange-800 text-sm mb-2">📋 Admission Details</h5>
          <div className="grid grid-cols-2 gap-2 text-xs text-orange-700">
            <div>Student: <strong>{data.student_name}</strong></div>
            <div>Class: <strong>{data.class_name}{data.section ? `-${data.section}` : ''}</strong></div>
            {data.roll_number && <div>Roll No: <strong>{data.roll_number}</strong></div>}
            {data.class_teacher && <div>Teacher: <strong>{data.class_teacher}</strong></div>}
            {data.first_day_date && <div>First Day: <strong>{data.first_day_date}</strong></div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Welcome Video Generator
export default function WelcomeVideoGenerator({ token, schoolId, schoolName }) {
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    parent_phone: '',
    class_name: 'Class 1',
    section: '',
    roll_number: '',
    class_teacher: '',
    first_day_date: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    principal_name: '',
    message_template: ''
  });

  useEffect(() => {
    loadTemplate();
    loadHistory();
  }, [schoolId]);

  const loadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/welcome-video/template/${schoolId || 'default'}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTemplate(data.template);
        setTemplateForm({
          principal_name: data.template?.principal_name || '',
          message_template: data.template?.message_template || ''
        });
      }
    } catch (err) {
      console.error('Error loading template:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/welcome-video/history/${schoolId || 'default'}?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setHistory(data.generations || []);
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const saveTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/welcome-video/template/setup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          school_id: schoolId || 'default',
          principal_name: templateForm.principal_name,
          message_template: templateForm.message_template,
          enabled: true
        })
      });
      const data = await res.json();
      if (data.success) {
        setEditingTemplate(false);
        loadTemplate();
        alert('Template saved successfully!');
      }
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  const handleGenerate = async () => {
    if (!formData.student_name || !formData.parent_phone) {
      alert('Student name और Parent phone required है');
      return;
    }

    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/welcome-video/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          school_id: schoolId || 'default',
          ...formData
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedContent(data);
        loadHistory();
      }
    } catch (err) {
      console.error('Error generating:', err);
    }
    setGenerating(false);
  };

  const handleWhatsAppShare = () => {
    if (!generatedContent) return;
    const message = encodeURIComponent(generatedContent.personalized_message);
    const phone = formData.parent_phone.replace(/\D/g, '');
    window.open(`https://wa.me/91${phone}?text=${message}`, '_blank');
  };

  const handleCopyMessage = () => {
    if (!generatedContent) return;
    navigator.clipboard.writeText(generatedContent.personalized_message);
    alert('Message copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            🎬 Welcome Video Generator
          </h2>
          <p className="text-gray-500 text-sm">Personalized welcome messages for new admissions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowBulkUpload(!showBulkUpload); setShowHistory(false); setEditingTemplate(false); }}
            className={`px-4 py-2 rounded-lg ${showBulkUpload ? 'bg-green-500 text-white' : 'bg-green-100 hover:bg-green-200 text-green-700'}`}
          >
            📊 Bulk Upload
          </button>
          <button
            onClick={() => { setShowHistory(!showHistory); setShowBulkUpload(false); }}
            className={`px-4 py-2 rounded-lg ${showHistory ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            📜 History
          </button>
          <button
            onClick={() => { setEditingTemplate(!editingTemplate); setShowBulkUpload(false); }}
            className={`px-4 py-2 rounded-lg ${editingTemplate ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            ⚙️ Template
          </button>
        </div>
      </div>

      {/* Template Editor */}
      {editingTemplate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-4">
          <h3 className="font-semibold">📝 Edit Welcome Template</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Principal Name</label>
              <input
                type="text"
                value={templateForm.principal_name}
                onChange={(e) => setTemplateForm({...templateForm, principal_name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Dr. R.K. Sharma"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={saveTemplate} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
              💾 Save Template
            </button>
            <button onClick={() => setEditingTemplate(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Upload Panel */}
      {showBulkUpload && (
        <BulkUploadWelcome 
          token={token} 
          schoolId={schoolId}
          onComplete={(results) => {
            loadHistory();
          }}
        />
      )}

      {/* History Panel */}
      {showHistory && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="font-semibold mb-3">📜 Recent Generations ({history.length})</h3>
          {history.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((item, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{item.student_name}</span>
                    <span className="text-gray-500 text-sm ml-2">{item.class_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${item.delivered ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                      {item.delivered ? '✅ Delivered' : '⏳ Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No history yet</p>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="font-semibold mb-4">📝 Student Details</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student Name *</label>
                <input
                  type="text"
                  value={formData.student_name}
                  onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="राहुल शर्मा"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Name</label>
                <input
                  type="text"
                  value={formData.parent_name}
                  onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="श्री शर्मा"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent WhatsApp *</label>
              <input
                type="tel"
                value={formData.parent_phone}
                onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="98765 43210"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class *</label>
                <select
                  value={formData.class_name}
                  onChange={(e) => setFormData({...formData, class_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  {['Nursery', 'LKG', 'UKG', ...Array.from({length: 12}, (_, i) => `Class ${i + 1}`)].map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                <input
                  type="text"
                  value={formData.section}
                  onChange={(e) => setFormData({...formData, section: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Roll No</label>
                <input
                  type="text"
                  value={formData.roll_number}
                  onChange={(e) => setFormData({...formData, roll_number: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="23"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Teacher</label>
                <input
                  type="text"
                  value={formData.class_teacher}
                  onChange={(e) => setFormData({...formData, class_teacher: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Mrs. Sunita Gupta"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Day Date</label>
                <input
                  type="date"
                  value={formData.first_day_date}
                  onChange={(e) => setFormData({...formData, first_day_date: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating || !formData.student_name || !formData.parent_phone}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            >
              {generating ? '⏳ Generating...' : '🎬 Generate Welcome Message'}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <h3 className="font-semibold">👁️ Preview</h3>
          <WelcomePreview 
            data={formData} 
            template={template} 
            schoolName={schoolName || template?.school_name || 'Our School'}
          />

          {/* Generated Content Actions */}
          {generatedContent && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <span className="text-xl">✅</span>
                <span className="font-semibold">Message Generated Successfully!</span>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleWhatsAppShare}
                  className="flex-1 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 flex items-center justify-center gap-2"
                >
                  💬 Send on WhatsApp
                </button>
                <button
                  onClick={handleCopyMessage}
                  className="px-4 py-3 bg-gray-200 rounded-xl hover:bg-gray-300"
                >
                  📋 Copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Generation Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
          💡 Pro Tip: Bulk Generation
        </h4>
        <p className="text-blue-700 text-sm mt-1">
          Multiple admissions के लिए Excel upload feature जल्द आ रहा है। 
          अभी एक-एक करके generate करें।
        </p>
      </div>
    </div>
  );
}
